import React from 'react';
import {
  createBrowserRouter,
  type LoaderFunctionArgs,
  Outlet,
  redirect,
  RouterProvider,
  useLocation,
  useRouteError,
} from 'react-router-dom';

import FullPageLoader from '../../foundation/components/full_page_loader/FullPageLoader';
import NotFound from '../../foundation/components/not_found/NotFound';
import { buildLoginUrl } from '../../foundation/utils/loginRedirect';
import {
  addItemToStorage,
  clearStorage,
  getItemFromStorage,
} from '../../foundation/utils/storageHandler';
import { store } from '../../store';
import { globalDataApi } from '../api/globalDataApi';
import { variantApi } from '../api/variantApi';
import ResetPasswordPage from '../authentication/login/ResetPasswordPage';
import { getAuthSession } from '../authentication/redux/async_thunks';
import { selectUsername } from '../authentication/redux/selectors';
import { setUsername } from '../authentication/redux/slice';
import Landing from '../landing/Landing';
import Layout from '../layout/Layout';
import LegalRequirements from '../legal/LegalRequirements';
import ProbandsSamplesPage from '../probands_samples/ProbandsSamplesPage';
import RunningTasks from '../running_tasks/RunningTasks';
import SampleDetailPage from '../sample_detail/SampleDetailPage';
import VariantCheck from '../variant_check/VariantCheck';
import ClinicalObservation from '../variant_workflow/clinical_observation/ClinicalObservation';
import VariantWorkflowWrapper from '../variant_workflow/components/VariantWorkflowWrapper';
import { VARIANT_CONSEQUENCE_TYPES } from '../variant_workflow/constants';
import FrequencyData from '../variant_workflow/frequency_data/FrequencyData';
import MainInterpretation from '../variant_workflow/interpretation/index';
import MolecularImpact from '../variant_workflow/molecular_impact/MolecularImpact';
import {
  resetVariantDependentData,
  setInstitutes,
} from '../variant_workflow/redux';
import Summary from '../variant_workflow/summary/Summary';
import VcfUploadPage from '../vcf_upload/VcfUploadPage';
/**
 * Root route loader: restores the session and prefetches global reference
 * data into the RTK Query cache. Runs once per app start.
 *
 * All matched loaders start in parallel, so on a hard reload `requireAuth`
 * runs before the session is restored here. The cached promise below lets
 * `requireAuth` await this exact run.
 * Quelle: https://reactrouter.com/start/data/route-object#loader
 */
let globalDataPromise: Promise<{ username?: string }> | null = null;

const initializeGlobalData = (): Promise<{ username?: string }> => {
  if (!globalDataPromise) {
    globalDataPromise = runGlobalDataInit();
  }
  return globalDataPromise;
};

const runGlobalDataInit = async (): Promise<{ username?: string }> => {
  try {
    const statusData = await store.dispatch(getAuthSession()).unwrap();

    if (statusData.meta?.is_authenticated) {
      let resolvedUsername = statusData.data?.user?.username;

      if (!resolvedUsername) {
        const unparsedUsername = getItemFromStorage('_u');
        if (unparsedUsername) {
          resolvedUsername = JSON.parse(unparsedUsername);
        }
      }

      if (resolvedUsername) {
        store.dispatch(setUsername(resolvedUsername));
        await store
          .dispatch(
            globalDataApi.endpoints.getUserDetails.initiate({
              username: resolvedUsername,
            }),
          )
          .unwrap();
        addItemToStorage('_u', JSON.stringify(resolvedUsername));
      }
    } else {
      clearStorage();
    }
  } catch (error) {
    clearStorage();
    console.error('Initial setup failed:', error);
    return { username: undefined };
  }

  const username = selectUsername(store.getState());
  if (!username) {
    return { username: undefined };
  }

  // Prefetch global reference data in parallel; allSettled keeps one failed
  // lookup from blocking app start.
  // Quelle: https://tkdodo.eu/blog/react-query-meets-react-router
  const prefetches: Promise<unknown>[] = [
    store
      .dispatch(globalDataApi.endpoints.getZygosityOptions.initiate())
      .unwrap(),
    store
      .dispatch(globalDataApi.endpoints.getSegregationOptions.initiate())
      .unwrap(),
    store
      .dispatch(globalDataApi.endpoints.getMedicalInvestigations.initiate())
      .unwrap(),
    store
      .dispatch(
        globalDataApi.endpoints.getReportVariantCategoryOptions.initiate(),
      )
      .unwrap(),
    store
      .dispatch(globalDataApi.endpoints.getExternalDatabases.initiate())
      .unwrap(),
    store
      .dispatch(
        globalDataApi.endpoints.getExternalDatabaseClassifications.initiate(),
      )
      .unwrap(),
    store
      .dispatch(globalDataApi.endpoints.getPublicationTypes.initiate())
      .unwrap(),
    store
      .dispatch(globalDataApi.endpoints.getPredictionTools.initiate())
      .unwrap(),
    store
      .dispatch(
        globalDataApi.endpoints.getMolecularImpactHeaderDescriptions.initiate(),
      )
      .unwrap(),
  ];

  await Promise.allSettled(prefetches);

  return { username };
};

/**
 * Protected-route loader: redirects to the landing page when no session is
 * active. Checking in the loader avoids a flash of protected content.
 *
 * Awaits `initializeGlobalData()` so the session restore on hard reload has
 * finished, then reads `selectUsername` live from the store - the cached
 * promise resolves once and would not reflect a later login (`?login=1`).
 * Quelle: https://reactrouter.com/api/utils/redirect
 */
const requireAuth = async ({ request }: LoaderFunctionArgs) => {
  await initializeGlobalData();
  const username = selectUsername(store.getState());
  if (!username) {
    throw redirect(buildLoginUrl(request.url));
  }
  return null;
};

/**
 * Variant workflow loader: resets stale variant state, then awaits only the
 * variant detail (the page can't render without it). All other tab data is
 * dispatched in the background - "React Router is about when, data caching
 * libs are about what."
 * Quelle: https://tkdodo.eu/blog/react-query-meets-react-router
 */
const variantWorkflowLoader = async ({
  params,
  request,
}: LoaderFunctionArgs) => {
  const variantId = params.vid;

  if (!variantId) {
    throw redirect('/variant-check');
  }

  // Matched loaders run in parallel, so verify the session here as well
  // before requesting protected variant data.
  await initializeGlobalData();
  if (request.signal.aborted) {
    return null;
  }
  if (!selectUsername(store.getState())) {
    throw redirect(buildLoginUrl(request.url));
  }

  // Clear the previous variant before loading the new one, so the UI never
  // briefly shows stale data.
  store.dispatch(resetVariantDependentData());

  const variantDetail = await store
    .dispatch(
      variantApi.endpoints.getVariantDetail.initiate({
        variant_id: variantId,
      }),
    )
    .unwrap();

  // React Router aborts superseded loaders, but the RTK Query request may
  // still resolve. Do not let an obsolete result update the active variant.
  if (request.signal.aborted) {
    return null;
  }

  if (variantDetail?.institutes !== undefined) {
    store.dispatch(setInstitutes(variantDetail.institutes));
  }

  // Fire off the remaining tab data without awaiting; RTK Query dedupes
  // these calls, so mounted components share the same cache entries.
  store.dispatch(
    variantApi.endpoints.getVariantAssessmentStages.initiate({
      variant_id: Number(variantId),
    }),
  );
  store.dispatch(
    variantApi.endpoints.getVariantComments.initiate({ variant_id: variantId }),
  );
  store.dispatch(
    variantApi.endpoints.getInterpretationSnapshots.initiate({
      variant_id: variantId,
    }),
  );
  store.dispatch(
    variantApi.endpoints.getVariantHistory.initiate({ variant_id: variantId }),
  );
  store.dispatch(
    variantApi.endpoints.getVariantInterpretationZone.initiate({
      variant_id: variantId,
    }),
  );
  store.dispatch(
    variantApi.endpoints.getVariantLinkouts.initiate({ variant_id: variantId }),
  );
  store.dispatch(
    variantApi.endpoints.getVariantMolecularImpactHeader.initiate({
      variant_id: variantId,
    }),
  );
  store.dispatch(
    variantApi.endpoints.getVariantAssessmentArguments.initiate({
      variant_id: variantId,
    }),
  );
  store.dispatch(
    variantApi.endpoints.getVariantReferences.initiate({
      variant_id: variantId,
    }),
  );
  store.dispatch(
    variantApi.endpoints.getGeneFactsOverview.initiate({
      variant_id: variantId,
    }),
  );
  store.dispatch(
    variantApi.endpoints.getVariantFrequencies.initiate({
      variant_id: variantId,
    }),
  );
  store.dispatch(
    variantApi.endpoints.getMaxAFScorings.initiate({
      variant_id: variantId,
    }),
  );
  store.dispatch(
    variantApi.endpoints.getVariantPredictionTools.initiate({
      variant_id: variantId,
    }),
  );
  store.dispatch(
    variantApi.endpoints.getSpliceSiteAlteration.initiate({
      variant_id: variantId,
    }),
  );
  store.dispatch(
    variantApi.endpoints.getVariantProteinDomains.initiate({
      variant_id: variantId,
    }),
  );

  if (variantDetail?.gene_id) {
    store.dispatch(
      variantApi.endpoints.getGeneTopPatVarFrequencies.initiate({
        gene_id: variantDetail.gene_id,
      }),
    );
  }

  if (variantDetail?.consequence === VARIANT_CONSEQUENCE_TYPES.MISSENSE) {
    store.dispatch(
      variantApi.endpoints.getOtherVariants.initiate({
        variant_id: variantId,
      }),
    );
  }

  return { variantId, variantDetail };
};

const AppRootRoute = () => {
  return <Outlet />;
};

const VariantCheckWrapper = () => {
  const { state } = useLocation() as { state?: { __refresh?: number } };
  return <VariantCheck key={state?.__refresh ?? 'init'} />;
};

const ProbandsSamplesWrapper = () => {
  const { state } = useLocation() as { state?: { __refresh?: number } };
  return <ProbandsSamplesPage key={state?.__refresh ?? 'init'} />;
};

/**
 * Error boundary for the workflow route. "Accesses the error thrown during
 * an action, loader, or component render" - kept separate from the root
 * <NotFound /> because a failed variant load is not a missing page.
 * Quelle: https://reactrouter.com/api/hooks/useRouteError
 */
const VariantWorkflowErrorBoundary = () => {
  const error = useRouteError() as
    { status?: number; message?: string } | Error | unknown;
  const status =
    typeof error === 'object' && error !== null && 'status' in error
      ? (error as { status?: number }).status
      : undefined;
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'object' && error !== null && 'message' in error
        ? String((error as { message?: unknown }).message)
        : 'Variant could not be loaded.';

  return (
    <NotFound
      statusOverride={status === 403 ? '403' : '500'}
      subTitleOverride={message}
    />
  );
};

/**
 * Application data router. "Data Routers should not be held in React state"
 * - created once at module level. "We recommend all web projects use
 * createBrowserRouter."
 * Quelle: https://reactrouter.com/api/data-routers/createBrowserRouter
 * Quelle: https://reactrouter.docschina.org/routers/picking-a-router/
 */
export const router = createBrowserRouter([
  {
    id: 'root',
    path: '/',
    element: <AppRootRoute />,
    loader: initializeGlobalData,
    // "Loader data is automatically revalidated after certain events like
    // navigations" - opt out so global data loads only once per app start.
    // Quelle: https://reactrouter.com/start/data/route-object#shouldrevalidate
    shouldRevalidate: () => false,
    errorElement: <NotFound />,
    // Shown while the root loader fetches on first load.
    // Quelle: https://reactrouter.com/start/data/route-object#hydratefallbackelement
    hydrateFallbackElement: <FullPageLoader />,
    children: [
      {
        path: 'reset-password',
        element: <ResetPasswordPage />,
      },
      {
        element: <Layout />,
        children: [
          {
            index: true,
            element: <Landing />,
          },
          {
            path: 'legal',
            element: <LegalRequirements />,
          },
          {
            element: <Outlet />,
            loader: requireAuth,
            children: [
              {
                path: 'variant-check',
                element: <VariantCheckWrapper />,
              },
              {
                path: 'dashboard',
                element: <ProbandsSamplesWrapper />,
              },
              {
                path: 'samples/:sampleId',
                element: <SampleDetailPage />,
              },
              {
                path: 'upload-vcf',
                element: <VcfUploadPage />,
              },
              {
                path: 'running-tasks',
                element: <RunningTasks />,
              },
              {
                path: 'variant-workflow/:vid',
                id: 'variant-workflow',
                element: <VariantWorkflowWrapper />,
                loader: variantWorkflowLoader,
                // Skip re-running the loader when navigating between tabs of the
                // same variant (e.g. Summary → MolecularImpact → Summary).
                // RTK Query cache serves the data instantly in that case.
                // Only re-run when the variant ID actually changes.
                // Quelle: https://reactrouter.com/start/data/route-object#shouldrevalidate
                shouldRevalidate: ({ currentParams, nextParams }) =>
                  currentParams.vid !== nextParams.vid,
                errorElement: <VariantWorkflowErrorBoundary />,
                children: [
                  {
                    index: true,
                    element: <Summary />,
                  },
                  {
                    path: 'clinical_observation',
                    element: <ClinicalObservation />,
                  },
                  {
                    path: 'frequency_data',
                    element: <FrequencyData />,
                  },
                  {
                    path: 'molecular_impact',
                    element: <MolecularImpact />,
                  },
                  {
                    path: 'interpretation',
                    element: <MainInterpretation />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
]);

/**
 * Renders the data router. "The router prop should be a single router
 * instance created outside of the React tree."
 * Quelle: https://reactrouter.com/api/data-routers/RouterProvider
 * Quelle: https://reactrouter.com/start/data/installation
 */
const Startup = () => {
  return <RouterProvider router={router} />;
};

export default Startup;
